import { createApp } from 'vue';
import { VueHeadMixin, createHead } from '@unhead/vue';
import { BrowserTracing, init as SentryInit, setTag, setUser, vueRouterInstrumentation } from '@sentry/vue';
import { injectInviteReferralsScript } from '~/utils/AuthUtils';
import QLogger from '~/services/QLogger';
import { createPinia } from 'pinia';
import FloatingVue from 'floating-vue';
import VueLazyLoad from 'vue3-lazyload';
import dayjs from 'dayjs';
import i18next from 'i18next';
import i18nFetchBackend from 'i18next-fetch-backend';
import I18NextVue from 'i18next-vue';
import App from './App.vue';
import { sentryConfig } from 'utilities';

import './style.css';
import './assets/css/tailwind.css';
import './assets/css/katex.css';
import './assets/css/global.css';
import './assets/scss/tooltip-themes.scss';
import './assets/css/fontawesome.all.min.css';
import 'floating-vue/dist/style.css';
import 'shared-library/style.css';

import createVuexStore from './store/index';
import UserPlugin from './plugins/UserPlugin';
import EventBusPlugin from './plugins/EventBusPlugin';
import AxiosPlugin, { updateURLs } from './plugins/AxiosPlugin';
import UtilitiesPlugin from './plugins/UtilitiesPlugin';
import BrazePlugin from './plugins/BrazePlugin';
import clickOutsidePlugin from './plugins/v-click-outside';
import vOnboardTooltip from './plugins/v-onboard-tip';
import vAutoPositionPlugin from './plugins/AutoPosition';
import setupSentryUserDetails from './plugins/SentryPlugin';

// services
import getRouter from './services/router';
import $axios from './services/AxiosService';
import EventBus from './services/EventBus';
import { isProd, isPseudolocalizationEnabled, isSentryEnabled } from './utils/EnvUtils';
import { PromiseWithTimeout, getCurrentLanguageToUse, isLangCodePTBR } from './utils/Utilities';
import type { Language } from './services/i18n';
import { translator } from './services/i18n';
import installGTag from './services/gtag';
import setupDebuggers from './utils/DevDebugger';
import idleQueueService from './services/IdleQueueService';
import QRouterPlugin from './plugins/QRouter';
import $store from './services/StoreService';
import { fetchPlugin } from 'ssr-toolkit/src/unifiedFetch/index.js';
import { setStatusCodePlugin } from 'ssr-toolkit/src/responseUtils/index.js';
import UserRecordingService from './services/UserRecordingService';
import type UserService from './services/UserService';
import SupportedLocaleForDayJS from './config/DayjsConfig';
import Constants from './config/Constants';
import UserActionsService from './services/apis/UserActionsService';

const app = createApp(App);
const router = getRouter(app);
const store = createVuexStore();

setupDebuggers(app);

if (isSentryEnabled()) {
  SentryInit({
    app,
    dsn: 'https://f4055af1be6347b5a3b645683a6b50ff@o984659.ingest.sentry.io/4505148554412032',
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 0.0001,
    sampleRate: 1,
    ignoreErrors: sentryConfig.ignoredErrors,
    beforeBreadcrumb: sentryConfig.beforeBreadcrumb,
    autoSessionTracking: false,
    maxBreadcrumbs: 30,

    integrations: [
      new BrowserTracing({
        routingInstrumentation: vueRouterInstrumentation(router),
      }),
    ],

    denyUrls: sentryConfig.denyUrls,
  });
}

// enable setting up status code on ssr from context on any vue component
setStatusCodePlugin(app);
// For unifying SSR and CSR lifecycle
app.use(fetchPlugin);

// seo enablement
const head = createHead();
app.mixin(VueHeadMixin);
app.use(head);

app.use(store);
$store(store);

i18next.use(i18nFetchBackend);

app.use(I18NextVue, { i18next });

// pinia usage
const piniaStore = createPinia();
app.use(piniaStore);

app.use(EventBusPlugin);
app.use(AxiosPlugin);
app.use(UtilitiesPlugin);
installGTag(app, router);

// directives
app.use(VueLazyLoad, {
  loading: 'https://cf.quizizz.com/img/logos/new/logo_placeholder_sm.png',
  error: 'https://cf.quizizz.com/img/logos/new/logo_placeholder_sm.png',
});

// floating vue
const customFloatingVueOptions = {
  themes: {
    'tooltip': {
      delay: {
        show: 300,
        hide: 0,
      },
    },
    'error-tooltip-sm': {
      triggers: ['hover'],
    },
    'error-tooltip': {
      triggers: ['hover'],
    },
    'info-tooltip': {
      triggers: ['hover'],
      delay: {
        show: 300,
        hide: 0,
      },
    },
    'mystic-peak-tooltip': {
      triggers: ['hover'],
    },
    'info-popper': {
      triggers: ['hover'],
      delay: {
        show: 300,
        hide: 0,
      },
    },
    'warn-popper': {
      triggers: ['hover'],
    },
    'danger-popper': {
      triggers: ['hover'],
    },
  },
};
app.use(FloatingVue, customFloatingVueOptions);

app.directive('click-outside', clickOutsidePlugin);
app.directive('onboard-tip', vOnboardTooltip);
app.directive('auto-position', vAutoPositionPlugin);

function loadDayJSLocale(language: 'es' | 'en' | 'fr' | 'pl' | 'ru' | 'id' | 'ms' | 'tl' | 'de' | 'vi' | 'th' | 'az' | 'pt-br' | 'zh-CN' | 'zh-TW' | 'tr' | 'it' | 'ja' | 'kk') {
  if (SupportedLocaleForDayJS[language]) {
    SupportedLocaleForDayJS[language]().then(() => dayjs.locale(language));
  } else {
    dayjs.locale('en');
  }
}

async function initialLoad() {
  store.dispatch('root/localClientInit');
  updateURLs();
  let user: UserService;
  if (window.serverData?.user) {
    user = window.serverData.user;
  } else {
    user = await UserActionsService.getCurrentUser($axios);
  }
  if (user) {
    store.dispatch('root/setUser', user);
    EventBus.$emit('userSet', { user });
  }

  const locale: any = getCurrentLanguageToUse(user?.preferences?.lang);

  let localeForI18next = locale;

  if (isLangCodePTBR(locale)) {
    localeForI18next = 'pt-BR';
  }

  if (isPseudolocalizationEnabled()) {
    const { default: Pseudo } = await import('i18next-pseudo');
    i18next.use(new Pseudo({}));
  }

  await i18next.init({
    lng: localeForI18next,
    debug: !isProd(),
    interpolation: {
      prefix: '{$',
      suffix: '}',
    },
    supportedLngs: Constants.TranslatedLanguages.map((lang) => {
      if (isLangCodePTBR(lang.code)) {
        return 'pt-BR';
      }
      return lang.code;
    }),
    fallbackLng: 'en',
    backend: {
      loadPath: `${import.meta.env.VITE_TRANSLATION_SOURCE_FILE_URL}/{$lng}.json`,
      crossDomain: true,
    },
    postProcess: ['pseudo'],
    nsSeparator: ':Q:',
  });

  translator.setCurrentLanguage(locale as Language);
  loadDayJSLocale(locale);
  let translationMap;
  let englishMap: Record<string, any> = {};
  try {
    translationMap = await import(`../translations/${locale}.json`);
    if (!translationMap) {
      translationMap = await import('../translations/en.json');
    }
    // Pull the english translation map for non english languages
    if (locale !== 'en') {
      englishMap = await import('../translations/en.json');
    };
  } catch (e) {
    translationMap = await import('../translations/en.json');
  }

  store.commit('root/setTranslations', translationMap.default);
  store.commit('root/setEnglishMap', englishMap.default ?? {});

  app.use(UserPlugin, { user, setUser, setTag });
  // Braze plugin needs User to be set
  app.use(BrazePlugin, { user, store });
  // has to move it here because route guards are dependent on user plugin
  app.use(QRouterPlugin(router));

  try {
    const promise = injectInviteReferralsScript();
    await PromiseWithTimeout(3000, promise).catch(ex =>
      QLogger.log('Errored while loading IR script', ex),
    );
  } catch (ex) {
    QLogger.error('Error while loading IR script', ex);
  }
}

(async () => {
  await initialLoad();
  const appDOM = document.querySelector('#app') as HTMLDivElement;
  app.mount(appDOM);
  idleQueueService.addBgTask(UserRecordingService.init);
  setupSentryUserDetails(store);
})();
